import {
  SLOT_CATEGORY_KEYS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
  type SlotCategoryKey,
} from 'utils/slot-categories';
import type {
  AreaConfigResponse,
  AreaSelection,
  DiscountRateCombination,
  DiscountRateEntry,
  DiscountRateLine,
  DiscountRateScenarioFormData,
  DiscountRateScenarioResponse,
  GeometryType,
  InterestType,
  RateCategory,
  ReserveCategory,
  RiskingEntry,
} from './types';
import {
  collapseEntries,
  combinationKey,
  expandLines,
  INTEREST_TYPES,
  PRE_PERMIT_RESERVE_CATEGORIES,
  RATE_CATEGORIES,
} from './discount-rate-line-utils';

export {
  hasNoHurdle,
  NO_HURDLE_RESERVE_CATEGORIES,
  PRE_PERMIT_RESERVE_CATEGORIES,
} from './discount-rate-line-utils';

export type FormMode = 'create' | 'edit';

export const GLOBAL_SELECTION_ID = 'global';

// ─────────────────────────────────────
// Constants
// ─────────────────────────────────────

export const DEFAULT_GEOMETRY_PRIORITY = [
  'Slot',
  'Unit',
  'Geology',
  'AOI',
  'TITLE',
  'StructureGrid',
];

// ─────────────────────────────────────
// Defaults
// ─────────────────────────────────────

export const DEFAULT_AREA_SELECTION: Omit<AreaSelection, 'id'> = {
  status: 'new',
  geometryType: 'area',
  configurableId: null,
  category: '',
  areaName: '',
  unitType: '',
  unitName: '',
  slotUwi: '',
  slotName: '',
  alias: '',
  notes: '',
  /**
   * The lines a new area starts with. A getter, so every spread of this object (Add Area) builds
   * its own lines (defaultAreaLines) and no two areas share one. Spread this only to create a new
   * area: a load, reset or fixture must set discountRateLines itself, or it silently gets the
   * default lines (default-area-selection-guard.test.ts fails when another source file uses it).
   * @return {DiscountRateLine[]} The default lines
   */
  get discountRateLines(): DiscountRateLine[] {
    return defaultAreaLines();
  },
  riskingEntries: [],
};

/**
 * The rate "Add Area" starts each reserve category at, by interest type, with no operator. Target,
 * Max and MTM each carry it (defaultAreaLines), and so do the WI / RI Defaults buttons. Keyed by
 * every slot category, so a category added to slot_categories.json fails to compile until it has
 * a default.
 */
export const DEFAULT_AREA_RATES: Record<
  SlotCategoryKey,
  Record<InterestType, number>
> = {
  pdp: { working_interest: 12.5, royalty_interest: 10 },
  duc: { working_interest: 20, royalty_interest: 15 },
  permit: { working_interest: 20, royalty_interest: 15 },
  // The pre-permit rates equal the LTD / Permit midpoint today, but product sets them explicitly.
  proposal: { working_interest: 22.5, royalty_interest: 17.5 },
  planned: { working_interest: 22.5, royalty_interest: 17.5 },
  pre_permit: { working_interest: 22.5, royalty_interest: 17.5 },
  apd: { working_interest: 22.5, royalty_interest: 17.5 },
  ltd: { working_interest: 25, royalty_interest: 20 },
};

/**
 * The lines a new area starts with: DEFAULT_AREA_RATES for every interest type and rate category,
 * grouped by collapseEntries as a saved area's entries are on load, so the area reads the same
 * after save and reopen. Built fresh per call, so no two areas share a line.
 * @param {() => number} newId - Id generator for the lines
 * @return {DiscountRateLine[]} The area's default lines
 */
export function defaultAreaLines(
  newId: () => number = nextId
): DiscountRateLine[] {
  const entries: DiscountRateCombination[] = INTEREST_TYPES.flatMap(
    (interestType) =>
      SLOT_CATEGORY_KEYS.flatMap((reserveCategory) =>
        RATE_CATEGORIES.map((rateCategory) => ({
          rateCategory,
          reserveCategory,
          operator: '',
          interestType,
          rate: DEFAULT_AREA_RATES[reserveCategory][interestType],
          hurdleRate: null,
        }))
      )
  );
  return collapseEntries(entries, newId);
}

export const DEFAULT_FORM_VALUES: DiscountRateScenarioFormData = {
  global: {
    name: '',
    effectiveDate: '',
    description: '',
    notes: '',
    geometryPriority: [...DEFAULT_GEOMETRY_PRIORITY],
  },
  areas: [],
};

let nextTempId = -1;

/**
 * Returns a unique negative ID for new client-side areas and entries.
 * Negative so it can never collide with a positive backend primary key —
 * for areas a collision makes the nav highlight/select an existing area; for
 * entries it makes buildUpdatePayload treat a new entry as an existing record.
 * @return {number} A negative temporary ID
 */
export function nextId(): number {
  return nextTempId--;
}

// ─────────────────────────────────────
// Helpers
// ─────────────────────────────────────

/**
 * Returns the display name for an area based on alias, area name, unit name, or slot name.
 * @param {AreaSelection} area - The area selection
 * @return {string} The display name
 */
export function getAreaDisplayName(area: AreaSelection): string {
  return (
    area.alias || area.areaName || area.unitName || area.slotName || 'New Area'
  );
}

/**
 * Returns the nav badge label based on geometry type and category/unitType.
 * @param {AreaSelection} area - The area selection
 * @return {string | undefined} The badge label
 */
export function getAreaBadge(area: AreaSelection): string | undefined {
  if (area.geometryType === 'slot') return 'Slot';
  if (area.geometryType === 'unit') return area.unitType || undefined;
  return area.category || undefined;
}

// ─────────────────────────────────────
// Validators
// ─────────────────────────────────────

/**
 * Validates that a string value is non-empty.
 * @param {string} value - The string to validate
 * @return {string | undefined} Error message or undefined if valid
 */
export function validateRequired(value: string): string | undefined {
  return value.trim() ? undefined : 'This field is required';
}

/**
 * Checks whether an area selection has all required fields filled.
 * @param {AreaSelection} area - The area to validate
 * @return {boolean} True if the area geometry selection is complete
 */
export function isAreaSelectionComplete(area: AreaSelection): boolean {
  if (area.geometryType === 'area') {
    return !!area.category && !!area.areaName;
  }
  if (area.geometryType === 'unit') {
    return !!area.unitType && !!area.unitName;
  }
  return !!area.slotUwi;
}

/**
 * Checks whether a risking entry has all required fields valid.
 * @param {RiskingEntry} e - The entry to validate
 * @return {boolean} True if the entry is valid
 */
export function isRiskingEntryValid(e: RiskingEntry): boolean {
  if (!e.riskType || !e.interestType) return false;
  if (
    e.riskPercentage === '' ||
    Number(e.riskPercentage) < 0 ||
    Number(e.riskPercentage) > 100
  )
    return false;
  if (e.riskType === 'title' && (!e.titleQuality || !e.titleRiskFactor))
    return false;
  if (e.riskType === 'general' && (e.titleQuality || e.titleRiskFactor))
    return false;
  return true;
}

/**
 * Checks for duplicate risking entries within an area.
 * Duplicate = same risk_type + interest_type.
 * @param {RiskingEntry[]} entries - The entries to check
 * @return {boolean} True if duplicates exist
 */
export function hasDuplicateRiskingEntries(entries: RiskingEntry[]): boolean {
  const keys = new Set<string>();
  for (const e of entries) {
    if (!e.riskType || !e.interestType) continue;
    const key = `${e.riskType}|${e.interestType}`;
    if (keys.has(key)) return true;
    keys.add(key);
  }
  return false;
}

const DEFAULT_RESERVE_CATEGORIES: SlotCategoryKey[] = [
  'pdp',
  'duc',
  'permit',
  'ltd',
];

/**
 * @param {DiscountRateCombination[]} current - The area's discount rate entries
 * @param {InterestType} interestType - The interest type being seeded
 * @param {RateCategory} rateCategory - The rate category
 * @param {SlotCategoryKey} reserve - The reserve category
 * @return {number | ''} The rate a default row starts at: a pre-permit row follows the area's own
 *   LTD / Permit midpoint when both are set; every other row, and a pre-permit row without them,
 *   takes its DEFAULT_AREA_RATES rate
 */
function defaultRowRate(
  current: DiscountRateCombination[],
  interestType: InterestType,
  rateCategory: RateCategory,
  reserve: SlotCategoryKey
): number | '' {
  if (PRE_PERMIT_RESERVE_CATEGORIES.includes(reserve)) {
    const midpoint = ltdPermitMidpoint(current, interestType, rateCategory);
    if (midpoint !== '') return midpoint;
  }
  return DEFAULT_AREA_RATES[reserve][interestType];
}

/**
 * The rows "Load defaults" adds to an area for one interest type: a row per rate category for
 * PDP, DUC, Permit and LTD, then one per rate category for each pre-permit category, each at its
 * DEFAULT_AREA_RATES rate. A pre-permit row instead takes the midpoint of the area's own LTD and
 * Permit rates for that rate category and interest type when both are set. Only operator-less
 * rows count, and a row the area already has is not added again.
 * @param {DiscountRateCombination[]} current - The area's discount rate entries
 * @param {InterestType} interestType - The interest type to seed
 * @param {() => number} newId - Id generator for the new rows
 * @return {DiscountRateEntry[]} The rows to append
 */
export function defaultDiscountRateEntries(
  current: DiscountRateCombination[],
  interestType: InterestType,
  newId: () => number = nextId
): DiscountRateEntry[] {
  const rows: DiscountRateEntry[] = [];
  for (const rateCategory of RATE_CATEGORIES) {
    for (const reserve of [
      ...DEFAULT_RESERVE_CATEGORIES,
      ...WELL_STATUS_SLOT_CATEGORY_KEYS,
    ]) {
      if (
        operatorlessRate(current, interestType, rateCategory, reserve) !==
        undefined
      )
        continue;

      rows.push({
        id: newId(),
        rateCategory,
        reserveCategory: reserve,
        operator: '',
        interestType,
        rate: defaultRowRate(current, interestType, rateCategory, reserve),
        hurdleRate: null,
      });
    }
  }
  return rows;
}

/**
 * @param {DiscountRateCombination} e - A combination
 * @param {InterestType} interestType - The interest type being seeded
 * @return {boolean} Whether "Load defaults" seeds its rate: an operator-less pre-permit
 *   combination of that interest type with no rate yet
 */
function isSeedablePrePermit(
  e: DiscountRateCombination,
  interestType: InterestType
): boolean {
  return (
    e.reserveCategory !== '' &&
    PRE_PERMIT_RESERVE_CATEGORIES.includes(e.reserveCategory) &&
    e.interestType === interestType &&
    !e.operator &&
    e.rate === ''
  );
}

/**
 * Groups default rows into lines: one per reserve category, except the pre-permit
 * categories, which share their lines (they are seeded at the same midpoint).
 * @param {DiscountRateCombination[]} rows - Default rows
 * @param {() => number} newId - Id generator for the lines
 * @return {DiscountRateLine[]} The lines
 */
function collapseDefaultRows(
  rows: DiscountRateCombination[],
  newId: () => number
): DiscountRateLine[] {
  const buckets = new Map<string, DiscountRateCombination[]>();
  for (const row of rows) {
    const bucket =
      row.reserveCategory !== '' &&
      PRE_PERMIT_RESERVE_CATEGORIES.includes(row.reserveCategory)
        ? 'pre_permit_categories'
        : row.reserveCategory;
    buckets.set(bucket, [...(buckets.get(bucket) ?? []), row]);
  }
  return [...buckets.values()].flatMap((bucket) =>
    collapseEntries(bucket, newId)
  );
}

/**
 * What "Load defaults" leaves an area with for one interest type. A line holding an
 * operator-less pre-permit combination still blank is seeded at the LTD / Permit midpoint once
 * both are set (and regrouped, since its combinations may now differ in rate); every other line
 * is kept as is. Then defaultDiscountRateEntries' new rows follow, at their default rates,
 * grouped into lines.
 * @param {DiscountRateLine[]} lines - The area's lines
 * @param {InterestType} interestType - The interest type to seed
 * @param {() => number} newId - Id generator for the new lines
 * @return {DiscountRateLine[]} The area's lines after the click
 */
export function loadDefaultLines(
  lines: DiscountRateLine[],
  interestType: InterestType,
  newId: () => number = nextId
): DiscountRateLine[] {
  const current = expandLines(lines);
  const kept = lines.flatMap((line) => {
    const combinations = expandLines([line]);
    let seeded = false;
    const filled = combinations.map((e) => {
      if (!isSeedablePrePermit(e, interestType)) return e;
      const rate = ltdPermitMidpoint(current, interestType, e.rateCategory);
      if (rate === '') return e;
      seeded = true;
      return { ...e, rate };
    });
    return seeded ? collapseEntries(filled, newId) : [line];
  });
  return [
    ...kept,
    ...collapseDefaultRows(
      defaultDiscountRateEntries(current, interestType, newId),
      newId
    ),
  ];
}

/**
 * Whether "Load defaults" would fill anything in for one interest type: an operator-less
 * pre-permit row still blank whose LTD / Permit midpoint can now be computed. The Defaults
 * button stays enabled while this holds, so the click after typing LTD and Permit can happen.
 * @param {DiscountRateCombination[]} current - The area's discount rate entries
 * @param {InterestType} interestType - The interest type
 * @return {boolean} Whether a pre-permit row is fillable
 */
export function hasFillablePrePermit(
  current: DiscountRateCombination[],
  interestType: InterestType
): boolean {
  return current.some(
    (e) =>
      isSeedablePrePermit(e, interestType) &&
      ltdPermitMidpoint(current, interestType, e.rateCategory) !== ''
  );
}

/**
 * @param {DiscountRateCombination[]} current - The area's discount rate entries
 * @param {InterestType} interestType - The interest type
 * @param {RateCategory} rateCategory - The rate category
 * @param {ReserveCategory} reserve - The reserve category
 * @return {number | '' | undefined} The area's operator-less rate, undefined when it has no row
 */
function operatorlessRate(
  current: DiscountRateCombination[],
  interestType: InterestType,
  rateCategory: RateCategory,
  reserve: ReserveCategory
): number | '' | undefined {
  return current.find(
    (e) =>
      e.rateCategory === rateCategory &&
      e.reserveCategory === reserve &&
      e.interestType === interestType &&
      !e.operator
  )?.rate;
}

/**
 * @param {DiscountRateCombination[]} current - The area's discount rate entries
 * @param {InterestType} interestType - The interest type
 * @param {RateCategory} rateCategory - The rate category
 * @return {number | ''} The midpoint of the area's LTD and Permit rates to the cent, blank
 *   unless both are set (10.1 and 10.2 give 10.15, not 10.149999999999999)
 */
function ltdPermitMidpoint(
  current: DiscountRateCombination[],
  interestType: InterestType,
  rateCategory: RateCategory
): number | '' {
  const ltd = operatorlessRate(current, interestType, rateCategory, 'ltd');
  const permit = operatorlessRate(
    current,
    interestType,
    rateCategory,
    'permit'
  );
  /**
   * A null rate counts as blank too: a row loaded with one would otherwise read as 0 (Number(null)).
   * @param {unknown} rate - A row's rate
   * @return {boolean} Whether it is unset
   */
  const blank = (rate: unknown) =>
    rate === undefined || rate === null || rate === '';
  if (blank(ltd) || blank(permit)) return '';
  return Math.round(((Number(ltd) + Number(permit)) / 2) * 100) / 100;
}

// ─────────────────────────────────────
// Configurable type mapping
// ─────────────────────────────────────

/**
 * Maps a frontend geometry type to the backend configurable_type string.
 * @param {GeometryType} geometryType - The frontend geometry type
 * @return {string} The backend configurable_type
 */
function toConfigurableType(geometryType: GeometryType): string {
  switch (geometryType) {
    case 'area':
      return 'AreaGeometry';
    case 'unit':
      return 'DevelopmentArea';
    case 'slot':
      return 'Well';
  }
}

/**
 * Maps a backend configurable_type to the frontend geometry type.
 * @param {string} configurableType - The backend configurable_type
 * @return {GeometryType} The frontend geometry type
 */
function fromConfigurableType(configurableType: string): GeometryType {
  switch (configurableType) {
    case 'AreaGeometry':
      return 'area';
    case 'DevelopmentArea':
      return 'unit';
    case 'Well':
      return 'slot';
    default:
      return 'area';
  }
}

// ─────────────────────────────────────
// Payload: Form → Backend (snake_case)
// ─────────────────────────────────────

/**
 * Maps a discount rate combination to the backend shape.
 * @param {DiscountRateCombination} e - The combination
 * @param {number} [id] - The saved row's id, when this updates one
 * @return {object} Backend entry payload
 */
function mapRateEntryToPayload(e: DiscountRateCombination, id?: number) {
  return {
    ...(id === undefined ? {} : { id }),
    rate_category: e.rateCategory,
    reserve_category: e.reserveCategory,
    operator: e.operator || null,
    interest_type: e.interestType,
    rate: e.rate,
    // A hurdle of 0 is a value the model accepts; only blank saves as null.
    hurdle_rate: e.hurdleRate === '' ? null : e.hurdleRate,
  };
}

/**
 * Expands an area's lines into discount_rate_entries attributes. A combination the area loaded
 * with carries its original id, wherever it now sits, so the save updates that row; every other
 * combination omits id so Rails creates it. Loaded rows no combination claims are destroyed.
 * @param {AreaSelection} area - A saved area
 * @return {object[]} Backend nested attributes
 */
function rateEntriesUpdatePayload(area: AreaSelection) {
  const idsByKey = area._originalDiscountRateEntryIdsByKey ?? {};
  const claimed = new Set<number>();

  const rows: Record<string, unknown>[] = expandLines(
    area.discountRateLines
  ).map((e) => {
    const id = idsByKey[combinationKey(e)];
    if (id === undefined || claimed.has(id)) return mapRateEntryToPayload(e);
    claimed.add(id);
    return mapRateEntryToPayload(e, id);
  });

  for (const id of area._originalDiscountRateEntryIds ?? []) {
    if (!claimed.has(id)) rows.push({ id, _destroy: true });
  }
  return rows;
}

/**
 * Maps a risking entry to the backend shape.
 * Only includes `id` for entries that came from the server (in originalIds).
 * @param {RiskingEntry} e - The entry
 * @param {number[]} originalIds - Server-assigned IDs from the initial load
 * @return {object} Backend entry payload
 */
function mapRiskingEntryToPayload(e: RiskingEntry, originalIds: number[]) {
  const payload: Record<string, unknown> = {
    risk_type: e.riskType,
    title_quality: e.titleQuality || null,
    title_risk_factor: e.titleRiskFactor || null,
    interest_type: e.interestType,
    risk_percentage: e.riskPercentage,
  };
  if (originalIds.includes(e.id)) payload.id = e.id;
  return payload;
}

/**
 * Maps an area to the backend shape for CREATE (no entry IDs needed).
 * @param {AreaSelection} area - The area selection
 * @return {object} Backend area config payload
 */
function mapNewAreaToPayload(area: AreaSelection) {
  return {
    configurable_type: toConfigurableType(area.geometryType),
    configurable_id: area.configurableId,
    alias_name: area.alias || null,
    notes: area.notes || null,
    discount_rate_entries_attributes: expandLines(area.discountRateLines).map(
      (e) => mapRateEntryToPayload(e)
    ),
    risking_entries_attributes: area.riskingEntries.map((e) =>
      mapRiskingEntryToPayload(e, [])
    ),
  };
}

/**
 * Builds the full payload for creating a new scenario (POST).
 * @param {DiscountRateScenarioFormData} values - The form values
 * @return {object} The wrapped payload
 */
export function buildCreatePayload(values: DiscountRateScenarioFormData) {
  const { global: g } = values;

  return {
    discount_rate_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: values.areas.map(mapNewAreaToPayload),
    },
  };
}

/**
 * Builds a diff payload for updating an existing scenario (PATCH).
 * For saved areas: includes server-assigned IDs on entries, computes _destroy
 * for removed entries, and omits IDs on newly added entries.
 * For new areas: sends all entries without IDs.
 * For destroyed areas: sends { id, _destroy: true }.
 * @param {DiscountRateScenarioFormData} values - Current form values
 * @param {number[]} removedAreaIds - Backend IDs of deleted areas
 * @return {object} The wrapped payload
 */
export function buildUpdatePayload(
  values: DiscountRateScenarioFormData,
  removedAreaIds: number[]
) {
  const { global: g } = values;
  const areaConfigsAttrs: Record<string, unknown>[] = [];

  // Destroyed areas
  for (const id of removedAreaIds) {
    areaConfigsAttrs.push({ id, _destroy: true });
  }

  for (const area of values.areas) {
    if (area.status === 'new') {
      // New area — no IDs on anything
      areaConfigsAttrs.push(mapNewAreaToPayload(area));
    } else {
      // Saved area — use original IDs to distinguish server vs client entries
      const origRiskIds = area._originalRiskingEntryIds ?? [];

      const riskEntries: Record<string, unknown>[] = area.riskingEntries.map(
        (e) => mapRiskingEntryToPayload(e, origRiskIds)
      );

      // Compute _destroy for entries that were in original but no longer present
      const currentRiskIds = new Set(
        area.riskingEntries
          .filter((e) => origRiskIds.includes(e.id))
          .map((e) => e.id)
      );
      for (const id of origRiskIds) {
        if (!currentRiskIds.has(id)) {
          riskEntries.push({ id, _destroy: true });
        }
      }

      areaConfigsAttrs.push({
        id: area.id,
        configurable_type: toConfigurableType(area.geometryType),
        configurable_id: area.configurableId,
        alias_name: area.alias || null,
        notes: area.notes || null,
        discount_rate_entries_attributes: rateEntriesUpdatePayload(area),
        risking_entries_attributes: riskEntries,
      });
    }
  }

  return {
    discount_rate_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: areaConfigsAttrs,
    },
  };
}

// ─────────────────────────────────────
// Response mapper: Backend → Form (camelCase)
// ─────────────────────────────────────

/**
 * @param {DiscountRateEntry[]} entries - An area's loaded entries
 * @return {Record<string, number>} Each combination's id; a repeated combination keeps its first
 */
function originalIdsByKey(
  entries: DiscountRateEntry[]
): Record<string, number> {
  const idsByKey: Record<string, number> = {};
  for (const e of entries) idsByKey[combinationKey(e)] ??= e.id;
  return idsByKey;
}

/**
 * Maps a backend area config response to the form's AreaSelection shape.
 * @param {AreaConfigResponse} ac - The area config from API response
 * @return {AreaSelection} The form area selection
 */
function mapAreaConfigToSelection(ac: AreaConfigResponse): AreaSelection {
  const geometryType = fromConfigurableType(ac.configurableType);
  const isArea = geometryType === 'area';
  const isUnit = geometryType === 'unit';
  const isSlot = geometryType === 'slot';

  const discountRateEntries: DiscountRateEntry[] = ac.discountRateEntries.map(
    (e) => ({
      id: e.id,
      rateCategory: e.rateCategory,
      reserveCategory: e.reserveCategory,
      operator: e.operator ?? '',
      interestType: e.interestType,
      rate: Number(e.rate),
      hurdleRate: e.hurdleRate === null ? null : Number(e.hurdleRate),
    })
  );

  return {
    id: ac.id,
    status: 'saved',
    geometryType,
    configurableId: ac.configurableId,
    category: isArea ? (ac.configurableCategory ?? '') : '',
    areaName: isArea ? (ac.configurableDisplayName ?? '') : '',
    unitType: isUnit ? (ac.configurableUnitType ?? '') : '',
    unitName: isUnit ? (ac.configurableDisplayName ?? '') : '',
    slotUwi: isSlot ? (ac.configurableDisplayName?.split(' — ')[0] ?? '') : '',
    slotName: isSlot ? (ac.configurableDisplayName ?? '') : '',
    alias: ac.aliasName ?? '',
    notes: ac.notes ?? '',
    discountRateLines: collapseEntries(discountRateEntries, nextId),
    riskingEntries: ac.riskingEntries.map((e) => ({
      id: e.id,
      riskType: e.riskType,
      titleQuality: e.titleQuality ?? '',
      titleRiskFactor: e.titleRiskFactor ?? '',
      interestType: e.interestType,
      riskPercentage: e.riskPercentage,
    })),
    _originalDiscountRateEntryIds: ac.discountRateEntries.map((e) => e.id),
    _originalDiscountRateEntryIdsByKey: originalIdsByKey(discountRateEntries),
    _originalRiskingEntryIds: ac.riskingEntries.map((e) => e.id),
  };
}

/**
 * Converts a backend scenario response into form data and geometries.
 * @param {DiscountRateScenarioResponse} res - The API response
 * @return {{ formData: DiscountRateScenarioFormData, geometries: Record<number, GeoJSON.Geometry> }}
 */
export function mapResponseToFormData(res: DiscountRateScenarioResponse): {
  formData: DiscountRateScenarioFormData;
  geometries: Record<number, GeoJSON.Geometry>;
} {
  const geometries: Record<number, GeoJSON.Geometry> = {};

  const areas = res.areaConfigs.map((ac) => {
    const selection = mapAreaConfigToSelection(ac);
    if (ac.configurableGeojson) {
      const geojson = ac.configurableGeojson;
      geometries[ac.id] =
        'geometry' in geojson && geojson.geometry
          ? geojson.geometry
          : (geojson as unknown as GeoJSON.Geometry);
    }
    return selection;
  });

  return {
    formData: {
      global: {
        name: res.name,
        effectiveDate: res.effectiveDate ?? '',
        description: res.description ?? '',
        notes: res.notes ?? '',
        geometryPriority: res.geometryPriority,
      },
      areas,
    },
    geometries,
  };
}
